import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Skeleton from '@atlaskit/skeleton';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context';
import { usePageContentId } from '@confluence/page-context';

import { useContentAttachments } from '../hooks/useContentAttachments';

import { DetailRow } from './DetailRow';
import { InlineRetryError } from './InlineRetryError';
import { SubtleLink } from './SubtleLink';

const i18n = defineMessages({
	attachments: {
		id: 'details-panel.links-section.attachments',
		defaultMessage: 'Attachments',
		description:
			'Text that appears next to a number indicating the number of attachments on specific content',
	},
});

const attachmentsLimit = 50;

export const AttachmentsRow = () => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const { data, loading, error, refetch } = useContentAttachments(attachmentsLimit);
	const { attachmentsCount, hasMoreAttachments } = data;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: `button`,
				actionSubjectId: 'SidePanelFiles',
				source: 'contentWrapper',
			},
		}).fire();
	};

	const renderFilesContent = () => {
		if (loading) {
			return (
				<UFOLoadHold name="attachments-row-details-panel-hold">
					<Skeleton width="48px" height="24px" borderRadius={3} testId="attachments-row-loading" />
				</UFOLoadHold>
			);
		}

		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		return (
			<SubtleLink
				href={`${CONTEXT_PATH}/pages/viewpageattachments.action?pageId=${contentId}`}
				onClick={onClick}
			>
				{attachmentsCount}
				{hasMoreAttachments ? '+' : ''}
			</SubtleLink>
		);
	};

	return (
		<DetailRow itemName={formatMessage(i18n.attachments)} icon={<AttachmentIcon label="" />}>
			{renderFilesContent()}
		</DetailRow>
	);
};
